/**
 * MARKETING COMPASS — PRE-FILL FROM PLATFORM DATA
 * ───────────────────────────────────────────────
 * Spec §3.3: where the Question Bank marks a question "Pre-fillable from
 * Narratoria", populate the answer from real platform data and present it as a
 * confirmed-but-editable answer. Pre-filled questions still appear in the quiz;
 * they are just one tap.
 *
 * ⚠️ NOTHING HERE IS INVENTED. Every pre-fill reads a field that genuinely
 * exists on the author's own documents today. Where the sheet expects a
 * pre-fill but the data model has no such field, the question falls back to
 * being asked normally and the shortfall is recorded in PREFILL_GAPS below
 * rather than papered over with a guess. A wrong pre-fill is worse than no
 * pre-fill: it is a wrong answer the member has been told is correct.
 *
 * Each pre-fill records its source field in `prefilled[qN]`, persisted on the
 * response doc exactly as spec §3.2 requires.
 */
import { GENRE_OPTIONS } from './marketingQuizConfig';

/**
 * Questions the sheet marks pre-fillable that this platform CANNOT fill, with
 * the reason. Surfaced to the member as "we couldn't fill this in" and to Julie
 * as a data-model gap list. Keep in sync with prefillAnswers() below.
 */
export const PREFILL_GAPS = [
  {
    questionId: 'q8',
    sheetSays: 'Partly',
    reason:
      'The only review data on the platform is ARC reviews collected through Narratoria. '
      + 'This gate is about RETAILER review counts (Robin Reads and similar require a minimum '
      + 'Amazon review count), which is a different number. Pre-filling an ARC count here would '
      + 'gate deal sites on the wrong figure.',
    wouldNeed: 'A retailer review count per book, entered by the author or pulled from a retailer API.',
  },
  {
    questionId: 'q9',
    sheetSays: 'Yes — distribution data',
    reason:
      'There is no KDP Select / wide field on author book documents. The Publishing Roadmap '
      + 'tracks a `distribution.strategy` value, but that lives in the roadmap tool\'s own '
      + 'per-licence progress store, not on the author\'s books, and is not available here.',
    wouldNeed: 'A `distribution` field (kdp_select | wide) on the book document.',
  },
  {
    questionId: 'q14',
    sheetSays: 'Partly — book metadata',
    reason:
      'Book documents carry `contentWarnings`, but that is a warnings list, not a heat scale — '
      + 'a sweet-clean book can carry warnings and a high-heat one may carry none. The CRM\'s '
      + '`resolvedTags.steamLevel` is closer, but it only exists in the superadmin reader-matching '
      + 'taxonomy, not on author book docs.',
    wouldNeed: 'A `heatLevel` field (sweet_clean | mid | high_heat | dark) on the book document.',
  },
];

/** Books that count as "out" — published and not archived. */
const publishedBooks = (books) => (books || []).filter((b) => b && b.status === 'launched');
const liveBooks = (books) => (books || []).filter((b) => b && b.status !== 'archived');

/* ── per-question derivations ───────────────────────────── */

/** Q1 — genre, from the genres tagged on the author's books. */
function genreFrom(books) {
  const known = new Map(GENRE_OPTIONS.map((g) => [g.toLowerCase(), g.toLowerCase()]));
  const found = new Set();

  liveBooks(books).forEach((b) => {
    (Array.isArray(b.genres) ? b.genres : []).forEach((g) => {
      const hit = known.get(String(g || '').trim().toLowerCase());
      if (hit) found.add(hit);
    });
  });

  // q1 is multi-select as of 2026-07-30 — every recognised genre is offered,
  // not collapsed into "multiple", because the engine ORs across them.
  if (found.size === 0) return null;                       // nothing recognisable → ask
  return { value: [...found], source: 'books.genres' };
}

/** Q2 — how many books are out, from the catalogue. */
function bookCountFrom(books) {
  if (!Array.isArray(books)) return null;
  const n = publishedBooks(books).length;
  const value = n === 0 ? '0' : n === 1 ? '1' : n <= 4 ? '2-4' : '5+';
  return { value, source: 'books.status==launched (count)' };
}

/**
 * Q3 — main goal. The sheet marks this "Partly — release dates", so this is a
 * SUGGESTION inferred from book status, not a fact: an unlaunched book implies
 * a launch push, an all-launched catalogue implies backlist selling. Anything
 * ambiguous is left unanswered rather than guessed.
 */
function goalFrom(books) {
  if (!Array.isArray(books) || !books.length) return null;
  const live = liveBooks(books);
  if (!live.length) return null;

  const upcoming = live.filter((b) => b.status === 'upcoming' || b.status === 'pre_launch_active');
  if (upcoming.length) return { value: 'launch', source: 'books.status (upcoming/pre-launch)' };

  const launched = live.filter((b) => b.status === 'launched');
  if (launched.length >= 2) return { value: 'sell_backlist', source: 'books.status (all launched)' };

  return null;    // a single launched book is genuinely ambiguous — ask
}

/**
 * Q7 — price band for this push, from the most recently added published book.
 * NOTE: `book.price` is the Narratoria direct-sale price, which is not
 * necessarily the retailer price being promoted — hence editable, and marked
 * "partly" in the sheet.
 */
function priceFrom(books) {
  const pub = publishedBooks(books)
    .filter((b) => b.price != null && b.price !== '')
    .sort((a, b) => (toMs(b.createdAt) - toMs(a.createdAt)));
  if (!pub.length) return null;

  const p = parseFloat(pub[0].price);
  if (!Number.isFinite(p)) return null;

  const value = p === 0 ? 'free' : p <= 2.99 ? '0_99_2_99' : 'full_price';
  return { value, source: 'books.price (most recent published)' };
}

/**
 * Q6 — email list size, from the author's own subscriber collection.
 *
 * ⚠️ DELIBERATE DEVIATION FROM THE SHEET. The Question Bank marks q6
 * "Pre-fillable from Narratoria? No", but that was written before the platform
 * had `authors/{id}/subscribers` (the GDPR-backed mailing list built from shop
 * opt-ins). Julie's call, 2026-07-29: platform truth beats the sheet. The drift
 * test records this as an intentional divergence rather than letting it read as
 * an accident.
 *
 * `null` means the count could not be read — unknown is not zero, so the
 * question falls back to being asked rather than asserting "No".
 */
function emailListFrom(subscriberCount) {
  if (subscriberCount == null || !Number.isFinite(subscriberCount)) return null;
  const n = subscriberCount;
  const value = n === 0 ? 'no'
    : n < 100 ? 'under_100'
      : n <= 1000 ? '100_1000'
        : '1000_plus';
  return { value, source: 'subscribers (count)' };
}

/** Q10 — audiobook status, from the audiobooks collection. */
function audiobookFrom(audiobooks) {
  if (!Array.isArray(audiobooks)) return null;
  if (!audiobooks.length) return { value: 'no', source: 'audiobooks (none)' };

  if (audiobooks.some((a) => a && a.status === 'live')) {
    return { value: 'yes', source: 'audiobooks.status==live' };
  }
  if (audiobooks.some((a) => a && (a.status === 'draft' || a.status === 'scheduled'))) {
    return { value: 'in_production', source: 'audiobooks.status==draft|scheduled' };
  }
  return { value: 'no', source: 'audiobooks (none live or in production)' };
}

/**
 * Q11 — marketing assets. Only "pro cover" has a platform source (`coverUrl`).
 * Brand kit, website, social accounts and video content have no field in the
 * data model, so they are left unticked for the author to fill in — this is a
 * partial pre-fill, not a claim that the others are absent.
 */
function assetsFrom(books) {
  const hasCover = liveBooks(books).some((b) => b && b.coverUrl);
  if (!hasCover) return null;
  return { value: ['pro_cover'], source: 'books.coverUrl', partial: true };
}

const toMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  const n = new Date(ts).getTime();
  return Number.isFinite(n) ? n : 0;
};

/* ── public API ─────────────────────────────────────────── */

/**
 * Derive pre-filled answers from the signed-in author's platform data.
 *
 * @param {{books?: Array, audiobooks?: Array, subscriberCount?: number|null}} data
 * @returns {{answers: object, prefilled: object, partial: string[], gaps: Array}}
 *   answers   — { qN: value } to seed the quiz with
 *   prefilled — { qN: sourceField } persisted on the response doc (spec §3.2)
 *   partial   — question ids where the pre-fill is incomplete and needs review
 *   gaps      — PREFILL_GAPS entries, for the "we couldn't fill this in" note
 */
export function prefillAnswers({ books = [], audiobooks = [], subscriberCount = null } = {}) {
  const derived = {
    q1: genreFrom(books),
    q2: bookCountFrom(books),
    q3: goalFrom(books),
    q6: emailListFrom(subscriberCount),
    q7: priceFrom(books),
    q10: audiobookFrom(audiobooks),
    q11: assetsFrom(books),
  };

  const answers = {};
  const prefilled = {};
  const partial = [];

  Object.entries(derived).forEach(([qid, result]) => {
    if (!result) return;
    answers[qid] = result.value;
    prefilled[qid] = result.source;
    if (result.partial) partial.push(qid);
  });

  return { answers, prefilled, partial, gaps: PREFILL_GAPS };
}

/** Human-readable note for a pre-filled question, shown beneath the answer. */
export function prefillNote(questionId, prefilled) {
  const source = prefilled && prefilled[questionId];
  if (!source) return null;
  if (questionId === 'q11') return "We've ticked what we can see from your books — add anything else.";
  if (questionId === 'q3') return "A guess from your book statuses — change it if that's not the plan.";
  if (questionId === 'q6') return "From your Narratoria subscriber list — count anything you hold elsewhere too.";
  if (questionId === 'q7') return "From your Narratoria price — change it if you're promoting at a different price.";
  return "We've filled this in from your books — tap to change.";
}
